/* ============================================================
   SAM-198 — "Policies" section, extracted out of ServiceEditorPage.tsx
   (which previously defined it inline) into its own file, matching the
   SchedulingSection/PricingSection/SessionsSection split done for the other
   sections other agents are touching concurrently in the same page.

   Field parity target: Rodrigo's 17 Sep proposal (adopted by Abel),
   `secBooking()` in
   ~/Desktop/xhinola-srl/confluence-12.4/SAM-services-admin-prototype/preview-proposed/services-flow.js
   ("Policies & Add-Ons"). Ground truth for what the backend actually reads:
   sam-lambda-functions@origin/dev, functions/booking/cancellation_policy.py
   (`evaluate()` reads only `time_limit` and `penalty_fee` off
   `settings.cancellation_policy` — no inside/outside branch, no approval,
   no payment-requirement, no waitlist, no age restriction, no terms) and
   functions/booking/engine_sync.py::_build_service_payload (reads only
   `cancellation_policy.time_limit`, as `cancelation_threshold`).

   Everything below Cancellation policy's Time Limit / Apply penalty fee is
   therefore UI-only parity: visually and functionally present (it saves and
   round-trips via settings.booking_policy, see useServiceEditor.ts), but
   covered by ONE compact "not enforced yet" notice at the top of the section
   (not one per field group — the prototype's own layout is dense enough
   without three separate amber boxes) so an admin never fills something
   silently ignored by booking behaviour. Add-Ons (prototype's `pFee*`
   extra-fees block) is SAM-590's ticket — shown here only as a disabled
   placeholder per that ticket's scope boundary.
   ============================================================ */
import React from 'react';
import { ShieldAlert, Trash2, Clock3, Tag } from 'lucide-react';
import { Notice } from '../../../components/ui/Notice';
import {
  Field, Grid2, PendingPill, SectionCard, type Editor,
} from '../ServiceEditorPage';
import { AddOnsEditor } from './AddOnsEditor';
import { penaltyExampleText } from '../../../utils/cancellationPenalty';

export function PoliciesSection({
  editor, mode, onDelete,
}: { editor: Editor; mode: 'create' | 'edit'; onDelete: () => void }) {
  const {
    formData, updateField, isDeleting, showDeleteConfirm, setShowDeleteConfirm, penaltyPercentIssue,
  } = editor;

  return (
    <>
      {/* One compact notice for every field below with no backend reader, instead of repeating
          the same paragraph after each field group (waitlist/approval/payment in "Booking flow",
          inside/outside-window in "Cancellation policy", age/terms further down) — kept the
          penalty-fee notice separate below since it's a different fact (a real, SAM-540-limited
          backend field, not an unread one). */}
      {/* Fields no backend reader consumes yet carry a per-field "Not active yet" pill (Field's
          `pending` prop) instead of one big amber banner with ticket numbers — the admin sees
          exactly which setting won't change booking behaviour, in plain language. */}
      <p className="text-[12px] mb-3" style={{ color: 'var(--ink-500)' }}>
        Settings marked <PendingPill /> are saved now and start working once bookings support them.
      </p>

      <SectionCard title="Booking flow" icon={<Clock3 size={14} />}>
        <label className="flex items-center gap-2 text-[13px] mb-3" style={{ color: 'var(--ink-700)' }}>
          <input
            type="checkbox"
            checked={formData.waitlist_enabled}
            onChange={(e) => updateField('waitlist_enabled', e.target.checked)}
          />
          Enable waitlist <PendingPill />
        </label>
        <Grid2>
          <Field label="Approval flow" pending>
            <select
              value={formData.approval_flow}
              onChange={(e) => updateField('approval_flow', e.target.value as typeof formData.approval_flow)}
            >
              <option value="instant">Instant booking</option>
              <option value="request">Request approval</option>
            </select>
          </Field>
          <Field label="Payment requirement" pending>
            <select
              value={formData.payment_requirement}
              onChange={(e) => updateField('payment_requirement', e.target.value as typeof formData.payment_requirement)}
            >
              <option value="free">Free to book</option>
              <option value="cc_hold">CC hold (no-show penalty)</option>
              <option value="deposit">Deposit / partial</option>
              <option value="full">Full prepay</option>
            </select>
          </Field>
        </Grid2>
        {(formData.payment_requirement === 'cc_hold' || formData.payment_requirement === 'deposit') && (
          <Grid2>
            <Field label="Amount ($ or %)">
              <input
                value={formData.payment_amount}
                onChange={(e) => updateField('payment_amount', e.target.value)}
                placeholder="e.g. 25 or 25%"
              />
            </Field>
            {formData.payment_requirement === 'cc_hold' ? (
              <Field label="Hold method">
                <select
                  value={formData.payment_hold_method}
                  onChange={(e) => updateField('payment_hold_method', e.target.value as typeof formData.payment_hold_method)}
                >
                  <option value="cc_auth">Credit card auth (hold)</option>
                  <option value="charge_refund">Charge upfront &amp; refund</option>
                </select>
              </Field>
            ) : <div />}
          </Grid2>
        )}
        {formData.payment_requirement === 'cc_hold' && (
          <Notice variant="info">
            An authorisation lapses in 5–7 days, which caps how far ahead a hold-protected
            reservation can be taken.
          </Notice>
        )}
      </SectionCard>

      <SectionCard title="Cancellation policy" icon={<ShieldAlert size={14} />}>
        {/* Matches the prototype's single 3-column "Cancellation policy" row (secBooking():
            Free-cancellation window / Inside the window / Outside the window) rather than
            splitting the window off into its own 2-col row. */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4">
          <Field label="Free-cancellation window (hours before)" caption="Guests cancel for free until this many hours before.">
            <input
              type="number"
              value={formData.cancellation_time_limit}
              onChange={(e) => updateField('cancellation_time_limit', e.target.value)}
              placeholder="24"
            />
          </Field>
          <Field label="Inside the window" pending>
            <select
              value={formData.cancellation_inside_window}
              onChange={(e) => updateField('cancellation_inside_window', e.target.value as typeof formData.cancellation_inside_window)}
            >
              <option value="auto">Refund automatically</option>
              <option value="request">Business approves</option>
            </select>
          </Field>
          <Field label="Outside the window" pending>
            <select
              value={formData.cancellation_outside_window}
              onChange={(e) => updateField('cancellation_outside_window', e.target.value as typeof formData.cancellation_outside_window)}
            >
              <option value="auto">Refund automatically</option>
              <option value="request">Business approves</option>
              <option value="none">No refund</option>
            </select>
          </Field>
        </div>

        {/* Apply penalty fee — real, SAM-540-limited backend field. Not part of the prototype's
            secBooking() (SAM never showed it there), kept on its own line rather than forced into
            the 3-col row above so it doesn't read as part of that not-enforced group. */}
        <label className="flex items-center gap-2 text-[13px] mt-3.5 mb-1" style={{ color: 'var(--ink-700)' }}>
          <input
            type="checkbox"
            checked={formData.cancellation_penalty_fee}
            onChange={(e) => updateField('cancellation_penalty_fee', e.target.checked)}
          />
          Apply penalty fee
        </label>
        {formData.cancellation_penalty_fee ? (
          // SAM-466 — penalty_percent: how much a late cancellation loses, as credit or as refund.
          <div className="mt-2" data-testid="penalty-percent">
            <Grid2>
              <Field
                label="Penalty on cancellation (credit or refund)"
                error={penaltyPercentIssue ?? undefined}
                caption="0 % = no penalty"
              >
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={100}
                  step={1}
                  value={formData.cancellation_penalty_percent}
                  onChange={(e) => updateField('cancellation_penalty_percent', e.target.value)}
                  placeholder="e.g. 50"
                  aria-invalid={penaltyPercentIssue ? true : undefined}
                />
              </Field>
              <div />
            </Grid2>
            <p className="text-[12px] -mt-1 mb-3.5" style={{ color: 'var(--ink-500)' }} data-testid="penalty-example">
              {penaltyExampleText(
                formData.cancellation_time_limit,
                formData.cancellation_penalty_fee,
                penaltyPercentIssue ? '' : formData.cancellation_penalty_percent,
              )}
            </p>
          </div>
        ) : (
          <p className="text-[12px] mb-3.5" style={{ color: 'var(--ink-500)' }}>
            Without a penalty, a late cancellation still gets a 100% credit.
          </p>
        )}

        <Grid2>
          <Field label="Age restriction" pending>
            <input
              value={formData.age_restriction}
              onChange={(e) => updateField('age_restriction', e.target.value)}
              placeholder="e.g. 18"
            />
          </Field>
          <div />
        </Grid2>
        <Field label="Terms &amp; conditions" pending>
          <textarea
            value={formData.terms_conditions}
            onChange={(e) => updateField('terms_conditions', e.target.value)}
            placeholder="Optional"
          />
        </Field>
      </SectionCard>

      <SectionCard title="Add-Ons" icon={<Tag size={14} />}>
        <AddOnsEditor editor={editor} />
      </SectionCard>

      {mode === 'edit' && (
        <section
          className="rounded-12 p-4"
          style={{
            border: '1px solid color-mix(in srgb, var(--danger) 35%, white)',
            borderRadius: 'var(--r-12)',
            background: 'var(--bad-bg)',
          }}
        >
          <h2 className="text-[13px] font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--bad-fg)' }}>
            <Trash2 size={14} /> Danger zone
          </h2>
          {!showDeleteConfirm ? (
            <button
              type="button"
              className="btn danger"
              disabled={isDeleting}
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 size={14} /> Delete Service
            </button>
          ) : (
            <div className="rounded-8 p-3 bg-white" style={{ borderRadius: 'var(--r-8)' }}>
              <Notice variant="error" className="mb-3">
                This action is permanent and cannot be undone.
              </Notice>
              <div className="flex gap-2">
                <button type="button" className="btn ghost flex-1 justify-center" disabled={isDeleting} onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </button>
                <button type="button" className="btn danger flex-1 justify-center" disabled={isDeleting} onClick={onDelete}>
                  <Trash2 size={14} /> {isDeleting ? 'Deleting…' : 'Confirm Delete'}
                </button>
              </div>
            </div>
          )}
        </section>
      )}
    </>
  );
}
